import React, { useState, useEffect } from 'react';
import { categoryService } from '../services/categoryService';

function SimplePieChart({ expenses, onCategorySelect, selectedCategory }) {
  const [categories, setCategories] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [hoveredSlice, setHoveredSlice] = useState(null);
  const [selectedSlice, setSelectedSlice] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [drillDownCategory, setDrillDownCategory] = useState(null); // Track which parent category we're drilling into
  const [viewMode, setViewMode] = useState('parent'); // 'parent' or 'subcategory'

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (categories.length > 0 && flatCategories.length > 0 && expenses.length > 0) {
      calculateChartData();
    }
  }, [expenses, categories, flatCategories, drillDownCategory, viewMode]);

  const loadCategories = async () => {
    try {
      // Load both hierarchical and flat categories
      const [hierarchicalData, flatData] = await Promise.all([
        categoryService.getCategories(),
        categoryService.getCategoriesFlat()
      ]);
      
      setCategories(hierarchicalData.categories || []);
      setFlatCategories(flatData.categories || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const calculateChartData = () => {
    // Group expenses by category and calculate totals
    const categoryTotals = {};
    let total = 0;
    let totalExpenses = 0;  // Track positive amounts (expenses)
    let totalCredits = 0;   // Track negative amounts (credits/refunds)

    if (viewMode === 'subcategory' && drillDownCategory) {
      // Show subcategories AND parent category expenses
      const parentCategory = categories.find(cat => cat.name === drillDownCategory);
      if (!parentCategory || !parentCategory.subcategories) {
        // No subcategories, go back to parent view
        setViewMode('parent');
        setDrillDownCategory(null);
        return;
      }

      // Filter expenses that belong to subcategories OR the parent itself
      expenses.forEach(expense => {
        // Handle split expenses
        if (expense.splits && Array.isArray(expense.splits)) {
          expense.splits.forEach(split => {
            const expenseCategory = flatCategories.find(cat => cat.name === split.categoryId);
            
            // Check if split belongs to a subcategory
            const isSubcategory = expenseCategory && expenseCategory.parentId && 
                parentCategory.subcategories.some(sub => sub.name === split.categoryId);
            
            // Check if split is directly assigned to the parent category
            const isParentCategory = split.categoryId === drillDownCategory;
            
            if (isSubcategory || isParentCategory) {
              // Use actual amount (negative for refunds/credits, positive for expenses)
              const amount = split.amount;
              if (!categoryTotals[split.categoryId]) {
                categoryTotals[split.categoryId] = 0;
              }
              categoryTotals[split.categoryId] += amount;
              total += amount;
              
              // Track expenses vs credits separately
              if (amount > 0) {
                totalExpenses += amount;
              } else {
                totalCredits += Math.abs(amount);
              }
            }
          });
        } else {
          // Handle simple expenses
          const expenseCategory = flatCategories.find(cat => cat.name === expense.category);
          
          // Check if expense belongs to a subcategory
          const isSubcategory = expenseCategory && expenseCategory.parentId && 
              parentCategory.subcategories.some(sub => sub.name === expense.category);
          
          // Check if expense is directly assigned to the parent category
          const isParentCategory = expense.category === drillDownCategory;
          
          if (isSubcategory || isParentCategory) {
            // Use actual amount (negative for refunds/credits, positive for expenses)
            const amount = expense.amount;
            if (!categoryTotals[expense.category]) {
              categoryTotals[expense.category] = 0;
            }
            categoryTotals[expense.category] += amount;
            total += amount;
            
            // Track expenses vs credits separately
            if (amount > 0) {
              totalExpenses += amount;
            } else {
              totalCredits += Math.abs(amount);
            }
          }
        }
      });
    } else {
      // Show parent categories (roll up subcategories into parents)
      expenses.forEach(expense => {
        // Handle split expenses
        if (expense.splits && Array.isArray(expense.splits)) {
          expense.splits.forEach(split => {
            const expenseCategory = flatCategories.find(cat => cat.name === split.categoryId);
            let categoryName = split.categoryId;
            
            // If this is a subcategory, roll it up to its parent
            if (expenseCategory && expenseCategory.parentId) {
              const parentCategory = categories.find(cat => 
                cat.subcategories && cat.subcategories.some(sub => sub.name === split.categoryId)
              );
              if (parentCategory) {
                categoryName = parentCategory.name;
              }
            }

            // Use actual amount (negative for refunds/credits, positive for expenses)
            const amount = split.amount;
            if (!categoryTotals[categoryName]) {
              categoryTotals[categoryName] = 0;
            }
            categoryTotals[categoryName] += amount;
            total += amount;
            
            // Track expenses vs credits separately
            if (amount > 0) {
              totalExpenses += amount;
            } else {
              totalCredits += Math.abs(amount);
            }
          });
        } else {
          // Handle simple expenses
          const expenseCategory = flatCategories.find(cat => cat.name === expense.category);
          let categoryName = expense.category;
          
          // If this is a subcategory, roll it up to its parent
          if (expenseCategory && expenseCategory.parentId) {
            const parentCategory = categories.find(cat => 
              cat.subcategories && cat.subcategories.some(sub => sub.name === expense.category)
            );
            if (parentCategory) {
              categoryName = parentCategory.name;
            }
          }

          // Use actual amount (negative for refunds/credits, positive for expenses)
          const amount = expense.amount;
          if (!categoryTotals[categoryName]) {
            categoryTotals[categoryName] = 0;
          }
          categoryTotals[categoryName] += amount;
          total += amount;
          
          // Track expenses vs credits separately
          if (amount > 0) {
            totalExpenses += amount;
          } else {
            totalCredits += Math.abs(amount);
          }
        }
      });
    }

    // Filter out categories with zero or negative net totals for pie chart display
    // (but keep the accurate total for display)
    const filteredCategoryTotals = Object.entries(categoryTotals).filter(([_, amount]) => amount > 0);

    // Calculate the total of positive amounts for percentage calculation
    const positiveTotal = filteredCategoryTotals.reduce((sum, [_, amt]) => sum + amt, 0);

    // Create chart data with category colors and icons
    const data = filteredCategoryTotals.map(([categoryName, amount]) => {
      let categoryInfo;
      
      if (viewMode === 'subcategory') {
        // Find subcategory info
        categoryInfo = flatCategories.find(cat => cat.name === categoryName);
      } else {
        // Find parent category info
        categoryInfo = categories.find(cat => cat.name === categoryName);
      }
      
      // Calculate percentage based on positive amounts only for pie chart
      // This ensures percentages always add up to 100%
      const percentage = positiveTotal > 0 ? ((amount / positiveTotal) * 100) : 0;

      return {
        name: categoryName,
        value: amount,
        percentage: parseFloat(percentage.toFixed(1)),
        color: categoryInfo?.color || '#95a5a6',
        icon: categoryInfo?.icon || '📦',
        formattedValue: formatCurrency(amount),
        hasSubcategories: viewMode === 'parent' && categoryInfo?.subcategories?.length > 0,
        isSubcategory: viewMode === 'subcategory'
      };
    });

    // Sort by value descending
    data.sort((a, b) => b.value - a.value);

    setChartData(data);
    setTotalAmount(total); // This is the net total (expenses - refunds)
    
    // Store expense and credit totals in state
    setTotalExpenses(totalExpenses);
    setTotalCredits(totalCredits);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleSliceClick = (index) => {
    const clickedCategory = chartData[index];
    
    if (viewMode === 'parent' && clickedCategory.hasSubcategories) {
      // If it's a parent category with subcategories, both drill down AND filter
      setDrillDownCategory(clickedCategory.name);
      setViewMode('subcategory');
      
      // Also filter the expense list by this parent category (includes subcategories)
      if (onCategorySelect) {
        onCategorySelect(clickedCategory.name);
      }
    } else {
      // If it's a leaf category (no subcategories) or we're in subcategory view, filter expenses
      if (onCategorySelect) {
        onCategorySelect(clickedCategory.name);
      }
    }
  };

  const handleBackToParent = () => {
    setViewMode('parent');
    setDrillDownCategory(null);
    
    // Clear the category filter when going back to parent view
    if (onCategorySelect) {
      onCategorySelect(null);
    }
  };

  // Create SVG pie chart
  const createPieSlices = () => {
    if (chartData.length === 0) return [];

    let cumulativePercentage = 0;
    const radius = 100;
    const centerX = 120;
    const centerY = 120;

    // For many categories, group small ones into "Others"
    let processedData = [...chartData];
    if (chartData.length > 8) {
      const topCategories = chartData.slice(0, 7);
      const otherCategories = chartData.slice(7);
      const othersTotal = otherCategories.reduce((sum, cat) => sum + cat.value, 0);
      
      // Calculate percentage based on the sum of all positive category totals
      const allCategoriesTotal = chartData.reduce((sum, cat) => sum + cat.value, 0);
      const othersPercentage = allCategoriesTotal > 0 ? ((othersTotal / allCategoriesTotal) * 100) : 0;

      processedData = [
        ...topCategories,
        {
          name: 'Others',
          value: othersTotal,
          percentage: parseFloat(othersPercentage.toFixed(1)),
          color: '#95a5a6',
          icon: '📦',
          formattedValue: formatCurrency(othersTotal),
          isOthers: true,
          count: otherCategories.length
        }
      ];
    }

    return processedData.map((item, index) => {
      const startAngle = (cumulativePercentage / 100) * 360;
      const endAngle = ((cumulativePercentage + item.percentage) / 100) * 360;

      const startAngleRad = (startAngle - 90) * (Math.PI / 180);
      const endAngleRad = (endAngle - 90) * (Math.PI / 180);

      const x1 = centerX + radius * Math.cos(startAngleRad);
      const y1 = centerY + radius * Math.sin(startAngleRad);
      const x2 = centerX + radius * Math.cos(endAngleRad);
      const y2 = centerY + radius * Math.sin(endAngleRad);

      const largeArcFlag = item.percentage > 50 ? 1 : 0;

      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');

      cumulativePercentage += item.percentage;

      const isHovered = hoveredSlice === index;
      const isCategorySelected = selectedCategory && item.name === selectedCategory;
      const isHighlighted = isHovered || isCategorySelected;

      return (
        <g key={index}>
          <path
            d={pathData}
            fill={item.color}
            stroke={isCategorySelected ? "#ff6b35" : "#2d2d2d"}
            strokeWidth={isHighlighted ? "3" : "2"}
            style={{
              cursor: 'pointer',
              opacity: isHighlighted ? '0.9' : '1',
              filter: isHighlighted ? 'brightness(1.1)' : 'none',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              setHoveredSlice(index);
              const rect = e.target.getBoundingClientRect();
              setTooltipPosition({
                x: rect.left + rect.width / 2,
                y: rect.top
              });
            }}
            onMouseLeave={() => {
              setHoveredSlice(null);
            }}
            onMouseMove={(e) => {
              const rect = e.target.getBoundingClientRect();
              setTooltipPosition({
                x: rect.left + rect.width / 2,
                y: rect.top
              });
            }}
            onClick={() => {
              handleSliceClick(index);
            }}
          />
          {/* Add percentage labels for slices > 5% */}
          {item.percentage > 5 && (
            <text
              x={centerX + (radius * 0.7) * Math.cos(((startAngle + endAngle) / 2 - 90) * (Math.PI / 180))}
              y={centerY + (radius * 0.7) * Math.sin(((startAngle + endAngle) / 2 - 90) * (Math.PI / 180))}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="white"
              fontSize={isHighlighted ? "11" : "10"}
              fontWeight="600"
              style={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                transition: 'all 0.2s ease',
                pointerEvents: 'none'
              }}
            >
              {item.percentage}%
            </text>
          )}
        </g>
      );
    });
  };

  // Tooltip component
  const renderTooltip = () => {
    const activeSlice = hoveredSlice;
    if (activeSlice === null) return null;

    let tooltipData;
    if (chartData.length > 8 && activeSlice >= 7 && !showAllCategories) {
      // Handle "Others" category
      const otherCategories = chartData.slice(7);
      const othersTotal = otherCategories.reduce((sum, cat) => sum + cat.value, 0);
      
      // Calculate percentage based on the sum of all positive category totals
      const allCategoriesTotal = chartData.reduce((sum, cat) => sum + cat.value, 0);
      const othersPercentage = allCategoriesTotal > 0 ? ((othersTotal / allCategoriesTotal) * 100) : 0;

      tooltipData = {
        name: 'Others',
        value: othersTotal,
        percentage: parseFloat(othersPercentage.toFixed(1)),
        color: '#95a5a6',
        icon: '📦',
        formattedValue: formatCurrency(othersTotal),
        isOthers: true,
        count: otherCategories.length,
        categories: otherCategories
      };
    } else {
      const processedData = chartData.length > 8 && !showAllCategories ? chartData.slice(0, 7) : chartData;
      tooltipData = processedData[activeSlice];
    }

    if (!tooltipData) return null;

    const tooltipStyle = {
      position: 'fixed',
      left: `${tooltipPosition.x}px`,
      top: `${tooltipPosition.y - 10}px`,
      transform: 'translate(-50%, -100%)',
      background: 'rgba(0, 0, 0, 0.95)',
      border: `2px solid ${tooltipData.color}`,
      borderRadius: '12px',
      padding: '16px',
      color: '#fff',
      fontSize: '14px',
      zIndex: 1000,
      minWidth: '200px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(10px)',
      pointerEvents: 'none'
    };

    return (
      <div style={tooltipStyle}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px'
        }}>
          <span style={{ fontSize: '24px' }}>{tooltipData.icon}</span>
          <div>
            <div style={{
              fontWeight: '700',
              fontSize: '16px',
              color: tooltipData.color
            }}>
              {tooltipData.name}
            </div>
            {tooltipData.isOthers && (
              <div style={{
                fontSize: '12px',
                color: '#b0b0b0'
              }}>
                {tooltipData.count} categories combined
              </div>
            )}
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px',
          marginBottom: tooltipData.isOthers ? '12px' : '0'
        }}>
          <div>
            <div style={{ color: '#b0b0b0', fontSize: '12px' }}>Amount</div>
            <div style={{ fontWeight: '700', color: tooltipData.color }}>
              {tooltipData.formattedValue}
            </div>
          </div>
          <div>
            <div style={{ color: '#b0b0b0', fontSize: '12px' }}>Percentage</div>
            <div style={{ fontWeight: '700', color: tooltipData.color }}>
              {tooltipData.percentage}%
            </div>
          </div>
        </div>

        {tooltipData.isOthers && tooltipData.categories && (
          <div>
            <div style={{
              color: '#b0b0b0',
              fontSize: '12px',
              marginBottom: '8px',
              borderTop: '1px solid #404040',
              paddingTop: '8px'
            }}>
              Includes:
            </div>
            <div style={{
              display: 'grid',
              gap: '4px',
              maxHeight: '120px',
              overflowY: 'auto'
            }}>
              {tooltipData.categories.slice(0, 5).map((cat, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '12px'
                }}>
                  <span>{cat.icon} {cat.name}</span>
                  <span style={{ color: cat.color }}>{cat.formattedValue}</span>
                </div>
              ))}
              {tooltipData.categories.length > 5 && (
                <div style={{
                  fontSize: '11px',
                  color: '#888',
                  textAlign: 'center',
                  marginTop: '4px'
                }}>
                  +{tooltipData.categories.length - 5} more...
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{
          fontSize: '11px',
          color: '#888',
          textAlign: 'center',
          marginTop: '8px',
          borderTop: '1px solid #404040',
          paddingTop: '8px'
        }}>
          {viewMode === 'parent' && tooltipData.hasSubcategories 
            ? 'Click to drill down and filter by this category (includes subcategories)'
            : viewMode === 'subcategory' || !tooltipData.hasSubcategories
              ? 'Click to filter expenses by this category'
              : 'Hover to explore'
          }
        </div>
      </div>
    );
  };

  if (expenses.length === 0) {
    return (
      <div style={{
        background: 'rgba(255, 107, 53, 0.05)',
        border: '1px solid #404040',
        borderRadius: '12px',
        padding: '24px',
        textAlign: 'center',
        marginBottom: '24px'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📊</div>
        <p style={{ color: '#b0b0b0', margin: '0' }}>
          No expenses to display in chart
        </p>
      </div>
    );
  }

  return (
    <div style={{
      background: 'rgba(255, 107, 53, 0.05)',
      border: '1px solid #404040',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h3 style={{
            color: '#f0f0f0',
            margin: '0',
            fontSize: '1.2rem',
            fontWeight: '600'
          }}>
            📊 Spending by Category
            {viewMode === 'subcategory' && (
              <span style={{ 
                color: '#ff6b35', 
                fontSize: '1rem',
                fontWeight: '400',
                marginLeft: '8px'
              }}>
                → {drillDownCategory}
              </span>
            )}
          </h3>
          
          {viewMode === 'subcategory' && (
            <button
              onClick={handleBackToParent}
              style={{
                background: 'rgba(255, 107, 53, 0.2)',
                border: '1px solid #ff6b35',
                color: '#ff6b35',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              ← Back to All Categories
            </button>
          )}
          
          {selectedCategory && (
            <button
              onClick={() => onCategorySelect && onCategorySelect(null)}
              style={{
                background: 'rgba(52, 152, 219, 0.2)',
                border: '1px solid #3498db',
                color: '#3498db',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              ✕ Clear Filter ({selectedCategory})
            </button>
          )}
          
          {chartData.length > 8 && (
            <button
              onClick={() => setShowAllCategories(!showAllCategories)}
              style={{
                background: 'rgba(255, 107, 53, 0.2)',
                border: '1px solid #ff6b35',
                color: '#ff6b35',
                padding: '4px 8px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              {showAllCategories ? 'Show Less' : `Show All ${chartData.length}`}
            </button>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          {totalCredits > 0 && (
            <>
              <div style={{
                color: '#2ecc71',
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '4px'
              }}>
                {formatCurrency(totalCredits)}
              </div>
              <div style={{
                color: '#b0b0b0',
                fontSize: '13px',
                marginBottom: '8px'
              }}>
                Credits/Refunds
              </div>
            </>
          )}
          <div style={{
            color: '#ff6b35',
            fontSize: '1.5rem',
            fontWeight: '700',
            marginBottom: '4px'
          }}>
            {formatCurrency(totalExpenses)}
          </div>
          <div style={{
            color: '#b0b0b0',
            fontSize: '13px',
            marginBottom: '8px'
          }}>
            Total Expenses
          </div>
          {totalCredits > 0 && (
            <div style={{
              borderTop: '1px solid #404040',
              paddingTop: '8px',
              marginTop: '4px'
            }}>
              <div style={{
                color: (totalCredits - totalExpenses) > 0 ? '#2ecc71' : '#ff4757',
                fontSize: '1.5rem',
                fontWeight: '700'
              }}>
                {formatCurrency(Math.abs(totalAmount))}
              </div>
              <div style={{
                color: '#b0b0b0',
                fontSize: '13px'
              }}>
                Net Total
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth <= 768 || chartData.length > 10 ? '1fr' : '280px 1fr',
        gap: window.innerWidth <= 768 ? '16px' : '24px',
        alignItems: 'flex-start'
      }}>
        {/* Always show the pie chart */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <svg
            width={window.innerWidth <= 480 ? "240" : "280"}
            height={window.innerWidth <= 480 ? "240" : "280"}
            style={{ maxWidth: '100%' }}
          >
            {createPieSlices()}
            {/* Center circle for donut effect */}
            <circle
              cx="120"
              cy="120"
              r="40"
              fill="#2d2d2d"
              stroke="#404040"
              strokeWidth="2"
            />
            <text
              x="120"
              y="110"
              textAnchor="middle"
              fill="#f0f0f0"
              fontSize={window.innerWidth <= 480 ? "12" : "14"}
              fontWeight="600"
            >
              {expenses.length}
            </text>
            <text
              x="120"
              y="125"
              textAnchor="middle"
              fill="#b0b0b0"
              fontSize={window.innerWidth <= 480 ? "9" : "11"}
            >
              expenses
            </text>
            <text
              x="120"
              y="140"
              textAnchor="middle"
              fill="#ff6b35"
              fontSize={window.innerWidth <= 480 ? "8" : "10"}
              fontWeight="600"
            >
              {chartData.length} categories
            </text>
          </svg>
        </div>

        {/* Legend/Category List */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: chartData.length > 10 ? 'repeat(auto-fit, minmax(250px, 1fr))' : '1fr',
          gap: '8px',
          maxHeight: chartData.length > 10 ? '300px' : 'none',
          overflowY: chartData.length > 10 ? 'auto' : 'visible',
          paddingRight: chartData.length > 10 ? '8px' : '0'
        }}>
          {chartData.slice(0, showAllCategories ? chartData.length : (chartData.length > 8 ? 7 : chartData.length)).map((item, index) => {
            const isCategorySelected = selectedCategory && item.name === selectedCategory;
            const isHighlighted = isCategorySelected;
            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px',
                  borderRadius: '8px',
                  background: isHighlighted ? 'rgba(255, 107, 53, 0.15)' : 'rgba(255, 107, 53, 0.05)',
                  border: `1px solid ${isHighlighted ? item.color : 'rgba(255, 107, 53, 0.1)'}`,
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!isHighlighted) {
                    e.target.style.background = 'rgba(255, 107, 53, 0.1)';
                    e.target.style.borderColor = item.color;
                  }
                  setHoveredSlice(index);
                }}
                onMouseLeave={(e) => {
                  if (!isHighlighted) {
                    e.target.style.background = 'rgba(255, 107, 53, 0.05)';
                    e.target.style.borderColor = 'rgba(255, 107, 53, 0.1)';
                  }
                  setHoveredSlice(null);
                }}
                onClick={() => {
                  handleSliceClick(index);
                }}
              >
                <div style={{
                  width: '18px',
                  height: '18px',
                  backgroundColor: item.color,
                  borderRadius: '4px',
                  flexShrink: 0,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  position: 'relative'
                }}>
                  {item.hasSubcategories && viewMode === 'parent' && (
                    <div style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-2px',
                      background: '#ff6b35',
                      color: '#fff',
                      borderRadius: '50%',
                      width: '12px',
                      height: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '8px',
                      fontWeight: '600'
                    }}>
                      ▼
                    </div>
                  )}
                </div>
                <span style={{ fontSize: '20px', flexShrink: 0 }}>{item.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: '#f0f0f0',
                    fontSize: '14px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {item.name}
                    {item.isSubcategory && (
                      <span style={{ color: '#b0b0b0', fontWeight: '400', fontSize: '12px' }}>
                        {' '}(subcategory)
                      </span>
                    )}
                    {item.hasSubcategories && viewMode === 'parent' && (
                      <span style={{ color: '#ff6b35', fontWeight: '400', fontSize: '12px' }}>
                        {' '}(click to drill down & filter)
                      </span>
                    )}
                    {!item.hasSubcategories && viewMode === 'parent' && (
                      <span style={{ color: '#3498db', fontWeight: '400', fontSize: '12px' }}>
                        {' '}(click to filter)
                      </span>
                    )}
                    {viewMode === 'subcategory' && (
                      <span style={{ color: '#3498db', fontWeight: '400', fontSize: '12px' }}>
                        {' '}(click to filter)
                      </span>
                    )}
                    {isCategorySelected && (
                      <span style={{ color: '#ff6b35', fontWeight: '600', fontSize: '12px' }}>
                        {' '}✓ filtering expenses
                      </span>
                    )}
                    {item.isOthers && (
                      <span style={{ color: '#b0b0b0', fontWeight: '400' }}>
                        {' '}({item.count} categories)
                      </span>
                    )}
                  </div>
                  <div style={{
                    color: '#b0b0b0',
                    fontSize: '12px'
                  }}>
                    {item.formattedValue}
                  </div>
                </div>
                <div style={{
                  background: `linear-gradient(135deg, ${item.color}20, ${item.color}40)`,
                  color: item.color,
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '700',
                  flexShrink: 0,
                  minWidth: '45px',
                  textAlign: 'center'
                }}>
                  {item.percentage}%
                </div>
              </div>
            );
          })}

          {/* Show "Others" category if there are more than 8 categories and not showing all */}
          {chartData.length > 8 && !showAllCategories && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px',
                borderRadius: '8px',
                background: 'rgba(149, 165, 166, 0.1)',
                border: '1px solid rgba(149, 165, 166, 0.2)'
              }}
            >
              <div style={{
                width: '18px',
                height: '18px',
                backgroundColor: '#95a5a6',
                borderRadius: '4px',
                flexShrink: 0,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}></div>
              <span style={{ fontSize: '20px', flexShrink: 0 }}>📦</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: '#f0f0f0',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  Others
                  <span style={{ color: '#b0b0b0', fontWeight: '400' }}>
                    {' '}({chartData.length - 7} categories)
                  </span>
                </div>
                <div style={{
                  color: '#b0b0b0',
                  fontSize: '12px'
                }}>
                  {formatCurrency(chartData.slice(7).reduce((sum, cat) => sum + cat.value, 0))}
                </div>
              </div>
              <div style={{
                background: 'rgba(149, 165, 166, 0.3)',
                color: '#95a5a6',
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '700',
                flexShrink: 0,
                minWidth: '45px',
                textAlign: 'center'
              }}>
                {(() => {
                  const othersTotal = chartData.slice(7).reduce((sum, cat) => sum + cat.value, 0);
                  const allCategoriesTotal = chartData.reduce((sum, cat) => sum + cat.value, 0);
                  const percentage = allCategoriesTotal > 0 ? ((othersTotal / allCategoriesTotal) * 100) : 0;
                  return percentage.toFixed(1);
                })()}%
              </div>
            </div>
          )}
        </div>
      </div>

      {chartData.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '16px',
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '8px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px',
            fontSize: '14px'
          }}>
            <div>
              <div style={{ color: '#b0b0b0' }}>Categories</div>
              <div style={{ color: '#f0f0f0', fontWeight: '600' }}>
                {chartData.length}
              </div>
            </div>
            <div>
              <div style={{ color: '#b0b0b0' }}>Largest Category</div>
              <div style={{ color: '#f0f0f0', fontWeight: '600' }}>
                {chartData[0]?.icon} {chartData[0]?.name}
              </div>
            </div>
            <div>
              <div style={{ color: '#b0b0b0' }}>Avg per Category</div>
              <div style={{ color: '#f0f0f0', fontWeight: '600' }}>
                {formatCurrency(totalAmount / chartData.length)}
              </div>
            </div>
            <div>
              <div style={{ color: '#b0b0b0' }}>Total Transactions</div>
              <div style={{ color: '#f0f0f0', fontWeight: '600' }}>
                {expenses.length}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Render tooltip */}
      {renderTooltip()}
    </div>
  );
}

export default SimplePieChart;